import type {ReactElement} from 'react';
import {useContext} from 'react';
import type {FormMakerImmutableStore} from '@FormMakerReact/form-maker.types';
import {EditorViewport} from '@FormMakerReact/form-maker.types';
import {formMakerContext} from '@FormMakerReact/form-maker.context';
import {useUndoRedoState} from '@FormMakerReact/hooks/use-undo-redo-state';
import {BackButton} from './components/back-button/back-button';
import {FormMakerEmbeddedNav} from './components/form-maker-embedded-nav/form-maker-embedded-nav';
import {HelpButton} from './components/help-button/help-button';
import {LogoLink} from './components/logo-link/logo-link';
import {PublishButton} from './components/publish-button/publish-button';
import {SaveBadge} from './components/save-badge/save-badge';
import {SaveButton} from './components/save-button/save-button';
import {ShareButton} from './components/share-button/share-button';
import {UndoRedoButtons} from './components/undo-redo-buttons/undo-redo-buttons';
import styles from './form-maker-header.module.scss';

function renderRightHeaderDividerExceptMobile(editorViewport: EditorViewport): ReactElement | null {
  if (editorViewport === EditorViewport.MOBILE) {
    return null;
  }
  return <div className={styles.divider} />;
}

/** The manual Save button is shown only while this session cannot autosave (an anon/free draft). */
function renderManualSaveButton(store: FormMakerImmutableStore): ReactElement | null {
  if (store.canAutosave) {
    return null;
  }
  return <SaveButton />;
}

export function FormMakerHeader(): ReactElement {
  const {state, actions, editor, store} = useContext(formMakerContext);
  const {canUndo, canRedo} = useUndoRedoState(editor);
  const {editorViewport} = state;
  if (store.isEmbedded) {
    return <FormMakerEmbeddedNav />;
  }
  const isCompact = editorViewport !== EditorViewport.DESKTOP;
  const headerClassName = isCompact ? `${styles.header} ${styles.headerCompact}` : styles.header;
  return (
    <header className={headerClassName} aria-label={window.i18next.t('pmwjs_form_maker_header_aria')}>
      <div className={styles.left}>
        <BackButton />
        <LogoLink />
        <div className={styles.divider} />
        <UndoRedoButtons actions={actions} canUndo={canUndo} canRedo={canRedo} />
      </div>
      <div className={styles.right}>
        <SaveBadge state={state} onRetry={actions.save} />
        {renderManualSaveButton(store)}
        {renderRightHeaderDividerExceptMobile(editorViewport)}
        <HelpButton />
        <ShareButton />
        <PublishButton />
      </div>
    </header>
  );
}
